import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { gregorianToJalaliStr } from "./PersianDatePicker";

const TYPES: Record<string, string> = {
  cash_asset: "دارایی نقدی", non_cash_asset: "سرمایه جاری", fixed_asset: "دارایی ثابت",
  income: "عواید", expense: "مصارف", liability: "قرضداری", equity: "سهم شرکا",
};

export async function generateAccountExcelReport(acc: any, txns: any[], dateFrom: string, dateTo: string) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "سیستم حسابداری";
  wb.created = new Date();

  const ws = wb.addWorksheet("گزارش حساب", {
    views: [{ rightToLeft: true, state: "frozen", ySplit: 9 }],
    pageSetup: { orientation: "landscape", fitToPage: true },
  });

  const NAVY = "1E293B";
  const ACCENT = "2563EB";
  const GREEN = "059669";
  const RED = "DC2626";
  const LIGHT = "F1F5F9";
  const WHITE = "FFFFFF";

  // 🆕 ستون‌ها بر اساس نوعیت حساب — ستون نامربوط برای آن نوع حساب ساخته نمی‌شود
  const isIncome = acc.type === "income";
  const isNonCashAsset = acc.type === "non_cash_asset" && acc.auto_link !== "customers_receivable";
  const isItemAccount = isIncome || isNonCashAsset;
  const isExpenseAccount = acc.type === "expense";
  const showExchange = !isIncome;

  const cols: { header: string; key: string; width: number }[] = [
    { header: "#", key: "idx", width: 6 },
    { header: "تاریخ", key: "date", width: 13 },
    { header: "شماره سند", key: "serial", width: 12 },
    { header: "نوع معامله", key: "type", width: 18 },
    { header: "طرف حساب / جزئیات", key: "party", width: 26 },
  ];
  if (isIncome) {
    cols.push(
      { header: "توضیحات", key: "notes", width: 26 },
      { header: "جنس/خدمت", key: "item_name", width: 20 },
      { header: "تعداد", key: "quantity", width: 9 },
      { header: "واحد", key: "unit", width: 8 },
      { header: "قیمت واحد", key: "unit_price", width: 12 },
      { header: "گدام", key: "warehouse", width: 14 },
      { header: "شماره موتر", key: "truck_plate", width: 12 },
      { header: "شماره بارنامه", key: "waybill_number", width: 13 },
    );
  } else if (isNonCashAsset) {
    cols.push(
      { header: "دسته‌بندی", key: "category_name", width: 16 },
      { header: "توضیحات", key: "notes", width: 26 },
      { header: "جنس/خدمت", key: "item_name", width: 20 },
      { header: "تعداد", key: "quantity", width: 9 },
      { header: "واحد", key: "unit", width: 8 },
      { header: "قیمت واحد", key: "unit_price", width: 12 },
      { header: "شماره موتر", key: "truck_plate", width: 12 },
      { header: "شماره بارنامه", key: "waybill_number", width: 13 },
    );
  } else if (isExpenseAccount) {
    cols.push(
      { header: "دسته‌بندی", key: "category_name", width: 16 },
      { header: "توضیحات", key: "notes", width: 30 },
    );
  } else {
    cols.push({ header: "توضیحات", key: "notes", width: 30 });
  }
  cols.push({ header: "ارز", key: "currency", width: 8 }, { header: "مبلغ", key: "amount", width: 16 });
  if (showExchange) cols.push({ header: "مبلغ اصلی", key: "orig_amount", width: 14 }, { header: "نرخ تبدیل", key: "rate", width: 11 });
  cols.push(
    { header: "ضمیمه", key: "attachment", width: 14 },
    { header: "بیلانس", key: "running", width: 16 },
  );
  ws.columns = cols.map(c => ({ key: c.key, width: c.width }));
  const totalCols = cols.length;

  // ── ردیف ۱: عنوان اصلی ──
  ws.mergeCells(1, 1, 2, totalCols);
  const titleCell = ws.getCell("A1");
  titleCell.value = `🏦  گزارش حساب: ${acc.name}`;
  titleCell.font = { name: "Arial", size: 20, bold: true, color: { argb: WHITE } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(1).height = 26; ws.getRow(2).height = 26;
  for (let r = 1; r <= 2; r++) for (let c = 1; c <= totalCols; c++)
    ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };

  // ── ردیف ۳: زیرعنوان ──
  ws.mergeCells(3, 1, 3, totalCols);
  const subCell = ws.getCell("A3");
  const period = (dateFrom || dateTo) ? `دوره: ${dateFrom ? gregorianToJalaliStr(dateFrom) : "ابتدا"} تا ${dateTo ? gregorianToJalaliStr(dateTo) : "انتها"}` : "دوره: کامل";
  subCell.value = `کد: ${acc.code}   |   نوعیت: ${TYPES[acc.type] || acc.type}   |   ارز: ${acc.currency}   |   ${period}   |   تاریخ تولید گزارش: ${new Date().toLocaleDateString("fa-IR")}`;
  subCell.font = { name: "Arial", size: 11, color: { argb: WHITE } };
  subCell.alignment = { horizontal: "center" };
  subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ACCENT } };
  ws.getRow(3).height = 20;

  // ── ردیف ۴: خالی ──
  ws.getRow(4).height = 8;

  // ── ردیف‌های ۵-۶: کارت‌های خلاصه ──
  const totalIn = txns.filter(t => t.direction === "in").reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const totalOut = txns.filter(t => t.direction === "out").reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const openBal = Number(acc.period_opening_balance ?? acc.opening_balance ?? 0);
  const openingLabel = dateFrom ? "🏦 بیلانس منتقل شده قبل از بازه" : "🏦 بیلانس اولیه";

  const summary: [string, string, string][] = [
    [openingLabel, openBal.toLocaleString("en-US", { minimumFractionDigits: 2 }), GREEN],
    ["▲ جمع ورودی‌ها", totalIn.toLocaleString("en-US", { minimumFractionDigits: 2 }), GREEN],
    ["▼ جمع خروجی‌ها", totalOut.toLocaleString("en-US", { minimumFractionDigits: 2 }), RED],
    ["💰 موجودی فعلی", Number(acc.balance).toLocaleString("en-US", { minimumFractionDigits: 2 }), ACCENT],
    ["🔢 تعداد تراکنش", String(txns.length), "7C3AED"],
  ];
  let col = 1;
  const span = Math.floor(totalCols / summary.length);
  summary.forEach(([label, value, color]) => {
    const startCol = col, endCol = col + span - 1;
    ws.mergeCells(5, startCol, 5, endCol);
    ws.mergeCells(6, startCol, 6, endCol);
    const lc = ws.getCell(5, startCol); lc.value = label;
    lc.font = { name: "Arial", size: 10, bold: true, color: { argb: "64748B" } };
    lc.alignment = { horizontal: "center" };
    const vc = ws.getCell(6, startCol); vc.value = value;
    vc.font = { name: "Arial", size: 14, bold: true, color: { argb: color } };
    vc.alignment = { horizontal: "center" };
    for (let r = 5; r <= 6; r++) for (let c = startCol; c <= endCol; c++) {
      ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT } };
      ws.getCell(r, c).border = {
        top: { style: "thin", color: { argb: "E2E8F0" } }, bottom: { style: "thin", color: { argb: "E2E8F0" } },
        left: { style: c === startCol ? "medium" : "thin", color: { argb: c === startCol ? color : "E2E8F0" } },
        right: { style: "thin", color: { argb: "E2E8F0" } },
      };
    }
    col = endCol + 1;
  });
  ws.getRow(5).height = 17; ws.getRow(6).height = 22;

  ws.getRow(7).height = 10; // خالی

  // ── ردیف ۸: عنوان جدول ──
  ws.mergeCells(8, 1, 8, totalCols);
  const tblTitle = ws.getCell("A8");
  tblTitle.value = "📋  جزئیات کامل تراکنش‌ها";
  tblTitle.font = { name: "Arial", size: 12.5, bold: true, color: { argb: NAVY } };
  tblTitle.border = { bottom: { style: "medium", color: { argb: ACCENT } } };
  ws.getRow(8).height = 20;

  // ── ردیف ۹: هدر جدول ──
  const headerRow = ws.getRow(9);
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.header;
    cell.font = { name: "Arial", size: 10.5, bold: true, color: { argb: WHITE } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = { top: { style: "thin" }, bottom: { style: "medium", color: { argb: ACCENT } }, left: { style: "thin", color: { argb: "334155" } }, right: { style: "thin", color: { argb: "334155" } } };
  });
  headerRow.height = 24;

  // ── ردیف بیلانس ابتدای گزارش ──
  let runBal = openBal;
  const openRow = ws.addRow({
    idx: "—", date: acc.created_at ? gregorianToJalaliStr(acc.created_at.split(" ")[0]) : "—",
    serial: "—", type: openingLabel, party: "", item_name: "", category_name: "", warehouse: "", truck_plate: "", waybill_number: "",
    currency: acc.currency, amount: openBal, orig_amount: "", rate: "", quantity: "", unit: "", unit_price: "",
    notes: "", attachment: "", running: openBal,
  });
  openRow.eachCell(cell => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D1FAE5" } };
    cell.font = { name: "Arial", size: 10, bold: true, color: { argb: GREEN } };
    cell.border = { bottom: { style: "medium", color: { argb: "86EFAC" } } };
  });
  openRow.getCell("amount").numFmt = "#,##0.00";
  openRow.getCell("running").numFmt = "#,##0.00";

  // ── ردیف‌های تراکنش ──
  const firstDataRow = ws.rowCount + 1;
  const amountColLetter = ws.getColumn("amount").letter;
  txns.forEach((t, i) => {
    const amt = Math.abs(Number(t.amount) || 0);
    runBal += t.direction === "in" ? amt : -amt;
    const hasAttachment = !!t.attachment;
    const row = ws.addRow({
      idx: i + 1,
      date: gregorianToJalaliStr(t.date),
      serial: t.serial || "—",
      type: (t.direction === "in" ? "▲ " : "▼ ") + t.type,
      party: t.party || "",
      item_name: t.item_name || "",
      category_name: t.category_name || "",
      warehouse: t.warehouse || "",
      truck_plate: t.truck_plate || "",
      waybill_number: t.waybill_number || "",
      currency: t.currency,
      amount: t.direction === "in" ? amt : -amt,
      orig_amount: t.orig_amount ? Number(t.orig_amount) : "",
      rate: t.exchange_rate && t.exchange_rate !== 1 ? Number(t.exchange_rate) : "",
      quantity: t.quantity || "",
      unit: t.unit || "",
      unit_price: t.unit_price ? Number(t.unit_price) : "",
      notes: t.notes || "",
      attachment: hasAttachment ? "دارد" : "—",
      running: runBal,
    });
    const isEven = i % 2 === 0;
    row.eachCell((cell, colNum) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isEven ? WHITE : LIGHT } };
      cell.font = { name: "Arial", size: 10, color: { argb: "1E293B" } };
      cell.border = { bottom: { style: "hair", color: { argb: "E2E8F0" } } };
      cell.alignment = { vertical: "middle" };
    });
    row.getCell("amount").numFmt = "#,##0.00";
    row.getCell("amount").font = { name: "Arial", size: 10.5, bold: true, color: { argb: t.direction === "in" ? GREEN : RED } };
    row.getCell("running").numFmt = "#,##0.00";
    row.getCell("running").font = { name: "Arial", size: 10.5, bold: true, color: { argb: runBal >= 0 ? GREEN : RED } };
    if (showExchange) row.getCell("orig_amount").numFmt = "#,##0.00";
    if (isItemAccount) row.getCell("unit_price").numFmt = "#,##0.00";
    if (hasAttachment) {
      row.getCell("attachment").font = { name: "Arial", size: 10, bold: true, color: { argb: ACCENT } };
    }
  });
  const lastDataRow = ws.rowCount;

  // ── ردیف جمع کل (SUM واقعی با فرمول) ──
  const sumRow = ws.addRow({
    idx: "", date: "", serial: "", type: "جمع کل", party: "", item_name: "", category_name: "", warehouse: "", truck_plate: "", waybill_number: "",
    currency: "",
    amount: { formula: `SUM(${amountColLetter}${firstDataRow}:${amountColLetter}${lastDataRow})` },
    orig_amount: "", rate: "", quantity: "", unit: "", unit_price: "", notes: "", attachment: "",
    running: "",
  });
  sumRow.eachCell((cell) => {
    cell.font = { name: "Arial", size: 11, bold: true, color: { argb: NAVY } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FEF3C7" } };
    cell.border = { top: { style: "double", color: { argb: NAVY } } };
  });
  sumRow.getCell("amount").numFmt = "#,##0.00";
  const currencyColIdx1 = cols.findIndex(c => c.key === "currency") + 1;
  if (currencyColIdx1 > 1) ws.mergeCells(sumRow.number, 1, sumRow.number, currencyColIdx1 - 1);

  // فوتر
  ws.addRow([]);
  const footRow = ws.addRow(["این گزارش به‌صورت سیستمی تولید شده است. ستون «ضمیمه» فقط نشان می‌دهد سند فایل پیوست دارد یا نه — دانلود مستقیم از این گزارش در نسخه بعدی اضافه می‌شود."]);
  ws.mergeCells(footRow.number, 1, footRow.number, totalCols);
  footRow.getCell(1).font = { name: "Arial", size: 9, italic: true, color: { argb: "94A3B8" } };
  footRow.getCell(1).alignment = { horizontal: "center" };

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: "application/octet-stream" }), `گزارش-${acc.name}-${acc.code}.xlsx`);
}
export async function generateAccountEntitiesExcelReport(acc: any, entities: any[]) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "سیستم حسابداری";
  wb.created = new Date();

  const ws = wb.addWorksheet("گزارش حساب", {
    views: [{ rightToLeft: true, state: "frozen", ySplit: 7 }],
    pageSetup: { orientation: "landscape", fitToPage: true },
  });

  const NAVY = "1E293B";
  const ACCENT = "2563EB";
  const GREEN = "059669";
  const RED = "DC2626";
  const LIGHT = "F1F5F9";
  const WHITE = "FFFFFF";

  const cols = [
    { header: "#", key: "idx", width: 6 },
    { header: "کد", key: "code", width: 12 },
    { header: "نام", key: "name", width: 30 },
    { header: "تلفن", key: "phone", width: 16 },
    { header: "شهر/یادداشت", key: "notes", width: 28 },
    { header: "ارز", key: "currency", width: 8 },
    { header: "بیلانس", key: "balance", width: 18 },
  ];
  ws.columns = cols.map(c => ({ key: c.key, width: c.width }));
  const totalCols = cols.length;

  ws.mergeCells(1, 1, 2, totalCols);
  const titleCell = ws.getCell("A1");
  titleCell.value = `🏦  گزارش حساب: ${acc.name}`;
  titleCell.font = { name: "Arial", size: 20, bold: true, color: { argb: WHITE } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(1).height = 26; ws.getRow(2).height = 26;
  for (let r = 1; r <= 2; r++) for (let c = 1; c <= totalCols; c++)
    ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };

  ws.mergeCells(3, 1, 3, totalCols);
  const subCell = ws.getCell("A3");
  subCell.value = `کد: ${acc.code}   |   ارز: ${acc.currency}   |   تاریخ تولید گزارش: ${new Date().toLocaleDateString("fa-IR")}`;
  subCell.font = { name: "Arial", size: 11, color: { argb: WHITE } };
  subCell.alignment = { horizontal: "center" };
  subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ACCENT } };
  ws.getRow(3).height = 20;

  ws.getRow(4).height = 8;

  const totalBal = entities.reduce((s, e) => s + Number(e.balance || 0), 0);
  const summary: [string, string, string][] = [
    ["💰 جمع بیلانس", totalBal.toLocaleString("en-US", { minimumFractionDigits: 2 }), ACCENT],
    ["🔢 تعداد اشخاص", String(entities.length), "7C3AED"],
  ];
  let col = 1;
  const span = Math.floor(totalCols / summary.length);
  summary.forEach(([label, value, color]) => {
    const startCol = col, endCol = col + span - 1;
    ws.mergeCells(5, startCol, 5, endCol);
    ws.mergeCells(6, startCol, 6, endCol);
    const lc = ws.getCell(5, startCol); lc.value = label;
    lc.font = { name: "Arial", size: 10, bold: true, color: { argb: "64748B" } };
    lc.alignment = { horizontal: "center" };
    const vc = ws.getCell(6, startCol); vc.value = value;
    vc.font = { name: "Arial", size: 13, bold: true, color: { argb: color } };
    vc.alignment = { horizontal: "center" };
    for (let r = 5; r <= 6; r++) for (let c = startCol; c <= endCol; c++) {
      ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT } };
      ws.getCell(r, c).border = { top: { style: "thin", color: { argb: "E2E8F0" } }, bottom: { style: "thin", color: { argb: "E2E8F0" } }, left: { style: "thin", color: { argb: "E2E8F0" } }, right: { style: "thin", color: { argb: "E2E8F0" } } };
    }
    col = endCol + 1;
  });
  ws.getRow(5).height = 16; ws.getRow(6).height = 20;

  const headerRow = ws.getRow(7);
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.header;
    cell.font = { name: "Arial", size: 10.5, bold: true, color: { argb: WHITE } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
  });
  headerRow.height = 22;

  const sorted = [...entities].sort((a, b) => Number(b.balance) - Number(a.balance));
  const firstDataRow = ws.rowCount + 1;
  sorted.forEach((en, i) => {
    const row = ws.addRow({
      idx: i + 1,
      code: en.code || "—",
      name: en.name || "—",
      phone: en.phone || "—",
      notes: [en.city, en.notes].filter(Boolean).join(" — ") || "—",
      currency: en.currency,
      balance: Number(en.balance) || 0,
    });
    const isEven = i % 2 === 0;
    row.eachCell(cell => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isEven ? WHITE : LIGHT } };
      cell.font = { name: "Arial", size: 10, color: { argb: "1E293B" } };
      cell.border = { bottom: { style: "hair", color: { argb: "E2E8F0" } } };
      cell.alignment = { vertical: "middle" };
    });
    row.getCell("balance").numFmt = "#,##0.00";
    row.getCell("balance").font = { name: "Arial", size: 10.5, bold: true, color: { argb: Number(en.balance) >= 0 ? GREEN : RED } };
    row.getCell("phone").alignment = { horizontal: "left" };
  });
  const lastDataRow = ws.rowCount;

  const balColLetter = ws.getColumn("balance").letter;
  const sumRow = ws.addRow({ idx: "", code: "", name: "جمع کل", phone: "", notes: "", currency: "", balance: { formula: `SUM(${balColLetter}${firstDataRow}:${balColLetter}${lastDataRow})` } });
  sumRow.eachCell(cell => {
    cell.font = { name: "Arial", size: 11, bold: true, color: { argb: NAVY } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FEF3C7" } };
    cell.border = { top: { style: "double", color: { argb: NAVY } } };
  });
  sumRow.getCell("balance").numFmt = "#,##0.00";
  ws.mergeCells(sumRow.number, 1, sumRow.number, 5);

  ws.addRow([]);
  const footRow = ws.addRow(["این گزارش به‌صورت سیستمی تولید شده است."]);
  ws.mergeCells(footRow.number, 1, footRow.number, totalCols);
  footRow.getCell(1).font = { name: "Arial", size: 9, italic: true, color: { argb: "94A3B8" } };
  footRow.getCell(1).alignment = { horizontal: "center" };

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: "application/octet-stream" }), `گزارش-${acc.name}-${acc.code}.xlsx`);
}